import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// TagInput — free-text or constrained multi-select with chip-style values and
// an inline suggestion popover. Used wherever the legacy MUI freeSolo
// Autocomplete used to live. `allowFreeText=true` lets the user submit values
// not in `options`; `false` rejects anything that isn't a known option.
// ---------------------------------------------------------------------------

export interface TagInputProps {
  label?: string;
  placeholder?: string;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  allowFreeText?: boolean;
}

export function TagInput({ label, placeholder, options, value, onChange, allowFreeText }: TagInputProps) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    return options
      .filter(o => !value.includes(o))
      .filter(o => q.length === 0 || o.toLowerCase().includes(q))
      .slice(0, 8);
  }, [options, value, input]);

  const addValue = (v: string) => {
    const trimmed = v.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) return;
    if (!allowFreeText && !options.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInput('');
  };

  const removeValue = (v: string) => {
    onChange(value.filter(x => x !== v));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (input.trim()) addValue(input);
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeValue(value[value.length - 1]);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <div className="relative">
        <Input
          value={input}
          placeholder={placeholder}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          // Delay blur so a click on a suggestion registers before the popover unmounts.
          onBlur={() => setTimeout(() => setFocused(false), 150)}
        />
        {focused && filteredSuggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md">
            {filteredSuggestions.map(opt => (
              <button
                key={opt}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addValue(opt); }}
                className="block w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map(v => (
            <Badge key={v} variant="secondary" className="gap-1 pr-1">
              {v}
              <button
                type="button"
                onClick={() => removeValue(v)}
                aria-label={`Remove ${v}`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChipPicker — toggleable pill row over a known option set. Used wherever a
// small fixed set of choices (weekdays, flag types) needs multi-select. Each
// chip is a button; selected chips render with primary tone, unselected with
// muted-bordered tone. Use TagInput instead when the option set is large or
// free-text values are allowed.
// ---------------------------------------------------------------------------

export interface ChipPickerProps {
  label?: string;
  options: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
}

export function ChipPicker({ label, options, value, onChange }: ChipPickerProps) {
  const toggle = (v: string) => {
    if (value.includes(v)) {
      onChange(value.filter(x => x !== v));
    } else {
      onChange([...value, v]);
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <div className="flex flex-wrap gap-1">
        {options.map(opt => {
          const selected = value.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={cn(
                'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                selected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-transparent text-muted-foreground hover:bg-muted',
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
